import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
// Reference mounts <CasinoRules> as a sibling after .casino-video (like Sic Bo) — shared RefVideo does that
import RefVideo from "../sicbo/RefVideo";
import MyBet from "../../casinoComponents/myBet";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import RouletteCardsOverlay from "../../casinoComponents/videoBox/overlays/RouletteCardsOverlay";
import ViewMoreModal from "../../../components/ViewMoreModal";
import RouletteBoard from "./RouletteBoard";
import ResultRoulette from "./ResultRoulette";
import { getCasinoGameDetails, getMyBets } from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import "../../../styles/games/roulette.css";
// Side-effect only: its unscoped `a { text-decoration: none; color: inherit }` rules are loaded
// app-wide (App.jsx imports this page eagerly) and every screen's links rely on them. Keep until
// they move to styles/index.css.
import "./OurRoullete.module.css";
import { formatBetNation } from "../../../utils/casinoMyBets";

// Reference game-header titles per API type (reference: lastresults2 res1.cname)
const ROULETTE_TITLES = {
  ourroullete: "Unique Roulette",
  roulette11: "VIP Roulette",
  roulette12: "Beach Roulette",
  roulette13: "Roulette",
};

/**
 * Roulette pages — casino/ourroullete, casino/roulette11, casino/roulette12, casino/roulette13 —
 * in the reference shell (.casino-table.roulette).
 */
export default function OurRoullete() {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "ourroullete";
  // "10 - Single", "1st 12 - Dozen", "3rd Column - Column" (reference)
  const formatNation = (nat, bet) => formatBetNation(gameName, nat, bet);

  const [gameData, setGameData] = useState(null);
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("Error fetching Roulette game data: ", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [gameName]);

  const matchId = gameData?.mid?.toString() || gameData?.data?.data?.mid?.toString() || gameData?.data?.mid?.toString();

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          setMyBets(
            response.bets.map((bet) => ({
              // My Bets "Gametype" column (casino_bets.game_name)
              description: bet.description ?? bet.game_name ?? bet.gameName,
              userName: bet.userName ?? bet.username,
              placeDate: bet.placeDate ?? bet.created_at,
              matchedBet: bet.player_name || bet.selection || "",
              odds: bet.odds || "0",
              stake: bet.stake || "0",
              type: bet.type || null,
              selection: bet.selection || bet.player_name || "",
              // Unique Roulette: the bet type by board position ("5,6 - Street")
              player_name: bet.player_name || "",
              exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
            })),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching my bets:", error);
    }
  }, [matchId]);

  useEffect(() => {
    if (!matchId) return;

    const timeoutId = setTimeout(fetchMyBetsData, 2000);
    const intervalId = setInterval(fetchMyBetsData, 2000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData, matchId]);

  // Admin panel places no bets (the old PlaceBet was a no-op stub): a board click refreshes the bets
  const handleBetClick = () => {
    fetchMyBetsData();
  };

  const feed = gameData?.data?.data ?? gameData?.data ?? {};
  const sub = Array.isArray(feed?.sub) ? feed.sub : [];
  const title = ROULETTE_TITLES[gameName] || ROULETTE_TITLES.ourroullete;
  const resultData = gameData?.lrs ?? feed?.lrs ?? [];
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;

  return (
    <CasinoTableLayout
      tableClass="roulette"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} formatNation={formatNation} />}
    >
      <>
        <CasinoHeading name={title} roundId={feed?.mid || ""} />{" "}
        <RefVideo
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          rulesType={gameName}
          cardsOverlay={feed?.card ? <RouletteCardsOverlay card={feed.card} /> : null}
        />{" "}
        {/* Reference renders .casino-detail once the feed has a round (casinoData.mid) */}
        {feed?.mid ? (
          <div className="casino-detail">
            <RouletteBoard sub={sub} lt={feed?.lt} onBetClick={handleBetClick} />
          </div>
        ) : null}{" "}
        {/* Reference: Last Result sits after .casino-detail, directly in .casino-table */}
        <CasinoLastResult
          gameType={gameName}
          viewAllLink={`/admin/reports/casinoresult/${gameName}`}
          resultComponent={<ResultRoulette gameType={gameName} data={resultData} title={title} />}
        />
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={formatNation}
      />
    </CasinoTableLayout>
  );
}
